import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });

const STATUS_LABELS = { trial: 'Trial', recipe_confirmed: 'Recipe Confirmed', commercial_trial: 'Commercial Trial', launched: 'Launched' };
const STATUS_COLORS = { trial: '#e67e00', recipe_confirmed: '#2e7d32', commercial_trial: '#1a56db', launched: '#7B2D6B' };
const STATUS_BG = { trial: '#fff3e0', recipe_confirmed: '#f0faf0', commercial_trial: '#e8f0fe', launched: '#f3e8ff' };

export default function CategoryReport() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubCategory, setSelectedSubCategory] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const r = await API.get('/reports/category', { params });
      setData(r.data);
    } catch { toast.error('Failed to load report'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const printPDF = () => {
    if (!data) return;
    const today = new Date().toLocaleDateString('en-AE', { day: '2-digit', month: 'short', year: 'numeric' });
    const userName = (() => { try { const u = JSON.parse(localStorage.getItem('npd_user')); return u?.name || u?.username || ''; } catch { return ''; } })();
    const dateRange = fromDate || toDate ? `${fromDate||'All'} — ${toDate||'Present'}` : 'All Dates';

    const groupSections = Object.entries(data.grouped).map(([group, products]) => {
      const rows = products.map((p, idx) => `
        <tr style="${idx%2===1?'background:#fdf9fc;':''}">
          <td style="font-family:'Courier New',monospace;font-size:9px;color:#7B2D6B;font-weight:600;padding:5px 8px;border-bottom:0.5px solid #f5edf3;">${p.product_code}</td>
          <td style="font-size:10px;font-weight:500;padding:5px 8px;border-bottom:0.5px solid #f5edf3;">${p.name}</td>
          <td style="padding:5px 8px;border-bottom:0.5px solid #f5edf3;text-align:center;">
            <span style="display:inline-block;padding:1px 8px;border-radius:20px;font-size:8px;font-weight:600;background:${STATUS_BG[p.status]||'#f5f5f5'};color:${STATUS_COLORS[p.status]||'#555'}">${STATUS_LABELS[p.status]||p.status}</span>
          </td>
          <td style="font-size:10px;padding:5px 8px;border-bottom:0.5px solid #f5edf3;color:#888;">${p.project_name||'—'}</td>
          <td style="font-size:10px;padding:5px 8px;border-bottom:0.5px solid #f5edf3;color:#888;">${p.department||'—'}</td>
        </tr>`).join('');

      return `
        <div style="margin-bottom:18px;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
            <div style="width:3px;height:14px;background:#7B2D6B;border-radius:2px;"></div>
            <div style="font-size:8px;letter-spacing:0.18em;text-transform:uppercase;font-weight:700;color:#7B2D6B;">${group}</div>
            <div style="background:#f9f3f8;padding:1px 8px;border-radius:10px;font-size:8px;color:#7B2D6B;font-weight:600;">${products.length} products</div>
            <div style="flex:1;height:1px;background:#7B2D6B;opacity:0.18;"></div>
          </div>
          <table>
            <thead><tr>
              <th style="width:85px;">Code</th><th>Product Name</th>
              <th style="width:120px;text-align:center;">Status</th>
              <th style="width:110px;">Project</th>
              <th style="width:90px;">Department</th>
            </tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>`;
    }).join('');

    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
    <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
    <style>
      *{box-sizing:border-box;margin:0;padding:0;}
      body{font-family:'Inter',Arial,sans-serif;background:#fff;padding:28px;color:#1a1a1a;font-size:11px;}
      .top-bar{height:4px;background:linear-gradient(90deg,#7B2D6B 0%,#a03880 55%,#E8541A 100%);border-radius:2px 2px 0 0;}
      .bottom-bar{height:4px;background:linear-gradient(90deg,#7B2D6B 0%,#a03880 55%,#E8541A 100%);border-radius:0 0 2px 2px;margin-top:12px;}
      table{width:100%;border-collapse:collapse;}
      th{padding:5px 8px;text-align:left;font-size:7px;letter-spacing:0.1em;text-transform:uppercase;color:#c0a0b8;font-weight:600;border-bottom:1px solid #e4d4e0;}
      @media print{body{padding:16px;} @page{margin:0;size:A4;} body{padding:20px 28px;}}
    </style></head><body>
    <div class="top-bar"></div>
    <div style="padding:12px 0 10px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #f0e8ed;margin-bottom:12px;">
      <div>
        <div style="font-size:6.5px;letter-spacing:0.22em;text-transform:uppercase;color:#E8541A;margin-bottom:3px;">Product Category Report</div>
        <div style="font-family:'Playfair Display',serif;font-size:13px;font-weight:700;color:#7B2D6B;">Kallada Food Industries LLC</div>
        <div style="font-size:8px;color:#c0a0b8;margin-top:1px;">Dubai, United Arab Emirates</div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:26px;font-weight:800;color:#7B2D6B;position:relative;display:inline-block;">BGH<span style="position:absolute;top:1px;right:-9px;width:7px;height:7px;background:#E8541A;border-radius:1.5px;display:inline-block;"></span></div>
        <div style="font-size:6px;letter-spacing:0.16em;text-transform:uppercase;color:#E8541A;font-weight:600;margin-top:3px;">Bettergrow Holding</div>
      </div>
    </div>
    <div style="background:#f9f3f8;border:1px solid #ede0ea;border-radius:3px;padding:8px 14px;margin-bottom:14px;display:flex;justify-content:space-between;align-items:center;">
      <div style="font-size:12px;font-weight:700;color:#7B2D6B;">By Internal Group</div>
      <div style="display:flex;gap:20px;">
        <div><div style="font-size:7px;color:#c0a0b8;text-transform:uppercase;letter-spacing:0.1em;">Date Range</div><div style="font-size:10px;font-weight:600;color:#333;">${dateRange}</div></div>
        <div><div style="font-size:7px;color:#c0a0b8;text-transform:uppercase;letter-spacing:0.1em;">Total Products</div><div style="font-size:10px;font-weight:600;color:#7B2D6B;">${data.total}</div></div>
        <div><div style="font-size:7px;color:#c0a0b8;text-transform:uppercase;letter-spacing:0.1em;">Groups</div><div style="font-size:10px;font-weight:600;color:#333;">${Object.keys(data.grouped).length}</div></div>
        <div><div style="font-size:7px;color:#c0a0b8;text-transform:uppercase;letter-spacing:0.1em;">Prepared By</div><div style="font-size:10px;font-weight:600;color:#333;">${userName}</div></div>
      </div>
    </div>
    ${groupSections}
    <div style="margin-top:10px;text-align:center;">
      <span style="font-size:7px;color:#d0bfca;letter-spacing:0.07em;text-transform:uppercase;">Kallada Food Industries LLC · Confidential Document · ${today}</span>
    </div>
    <div class="bottom-bar"></div>
    </body></html>`);
    win.document.close();
    win.focus();
  };

  const groups = data ? Object.keys(data.grouped).sort() : [];
  const getSubGroups = (products) => {
    const sub = {};
    products.forEach(p => {
      const s = p.sub_category || 'General';
      if (!sub[s]) sub[s] = [];
      sub[s].push(p);
    });
    return sub;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Product Category Report</div>
          <div className="page-subtitle">Products grouped by category and sub-category</div>
        </div>
        <button className="btn btn-primary" onClick={printPDF} disabled={!data}>⬇ Download / Print PDF</button>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="card-title">Filter by Date Range</div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label>From Date</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ width: 160 }} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>To Date</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ width: 160 }} />
          </div>
          <button className="btn btn-primary" onClick={load} disabled={loading}>
            {loading ? 'Loading...' : 'Apply Filter'}
          </button>
          <button className="btn btn-secondary" onClick={() => { setFromDate(''); setToDate(''); setTimeout(load, 100); }}>
            Clear
          </button>
          {data && <span className="text-muted" style={{ fontSize: 12 }}>{data.total} products · {groups.length} groups</span>}
        </div>
      </div>

      {/* Category Filter + View Toggle */}
      {data && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button onClick={() => { setSelectedCategory('all'); setSelectedSubCategory('all'); }} className={`btn btn-sm ${selectedCategory==='all' ? 'btn-primary' : 'btn-secondary'}`}>All</button>
            {groups.map(g => (
              <button key={g} onClick={() => { setSelectedCategory(g); setSelectedSubCategory('all'); }} className={`btn btn-sm ${selectedCategory===g ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: 11 }}>{g}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => setViewMode('list')} className={`btn btn-sm ${viewMode==='list' ? 'btn-primary' : 'btn-secondary'}`}>☰ List</button>
            <button onClick={() => setViewMode('tile')} className={`btn btn-sm ${viewMode==='tile' ? 'btn-primary' : 'btn-secondary'}`}>⊞ Tile</button>
          </div>
        </div>
      )}
      {data && selectedCategory !== 'all' && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          <button onClick={() => setSelectedSubCategory('all')} className={`btn btn-sm ${selectedSubCategory==='all' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: 10 }}>All</button>
          {[...new Set((data.grouped[selectedCategory]||[]).map(p => p.sub_category).filter(Boolean))].sort().map(s => (
            <button key={s} onClick={() => setSelectedSubCategory(s)} className={`btn btn-sm ${selectedSubCategory===s ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: 10 }}>{s}</button>
          ))}
        </div>
      )}

      {/* Groups */}
      {data && groups.length === 0 && (
        <div className="empty-state card"><div className="empty-state-icon">◫</div><p>No products found for selected date range.</p></div>
      )}

      {data && groups.filter(g => selectedCategory === 'all' || g === selectedCategory).map(group => (
        <div key={group} className="card mb-6">
          <div className="flex-between mb-4">
            <div className="flex gap-2" style={{ alignItems: 'center' }}>
              <div className="card-title mono" style={{ margin: 0, color: 'var(--accent)' }}>{group}</div>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{data.grouped[group].length} products</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {['trial','recipe_confirmed','commercial_trial','launched'].map(s => {
                const count = data.grouped[group].filter(p => p.status === s).length;
                if (!count) return null;
                return <span key={s} style={{ display:'inline-block', padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:600, background:STATUS_BG[s], color:STATUS_COLORS[s] }}>{STATUS_LABELS[s]}: {count}</span>;
              })}
            </div>
          </div>
          {Object.entries(getSubGroups(data.grouped[group])).sort().filter(([subgroup]) => selectedSubCategory === 'all' || subgroup === selectedSubCategory).map(([subgroup, subproducts]) => (
            <div key={subgroup} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8, paddingBottom: 4, borderBottom: '1px solid var(--border)' }}>
                {subgroup} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({subproducts.length})</span>
              </div>
              {viewMode === 'tile' ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                  {subproducts.map(p => (
                    <div key={p.product_code} onClick={() => window.location.href='/products/'+p.id}
                      style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', cursor: 'pointer', transition: 'all 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor='var(--accent)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}>
                      <div style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--accent)', fontWeight: 700, marginBottom: 4 }}>{p.product_code}</div>
                      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{p.name}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ display:'inline-block', padding:'2px 6px', borderRadius:20, fontSize:9, fontWeight:600, background:STATUS_BG[p.status]||'#f5f5f5', color:STATUS_COLORS[p.status]||'#555' }}>{STATUS_LABELS[p.status]||p.status}</span>
                        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{p.project_name||'—'}</span>
                      </div>
                      {p.trial_cost > 0 && <div style={{ fontSize: 11, color: 'var(--amber)', fontWeight: 600 }}>Dev: AED {p.trial_cost.toLocaleString('en-AE', { maximumFractionDigits: 0 })}</div>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr><th>Code</th><th>Product Name</th><th>Status</th><th>Project</th><th>Department</th></tr>
                    </thead>
                    <tbody>
                      {subproducts.map(p => (
                        <tr key={p.product_code} onClick={() => window.location.href='/products/'+p.id} style={{ cursor: 'pointer' }}>
                          <td className="mono">{p.product_code}</td>
                          <td style={{ fontWeight: 500 }}>{p.name}</td>
                          <td><span style={{ display:'inline-block', padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:600, background:STATUS_BG[p.status]||'#f5f5f5', color:STATUS_COLORS[p.status]||'#555' }}>{STATUS_LABELS[p.status]||p.status}</span></td>
                          <td className="text-muted">{p.project_name||'—'}</td>
                          <td className="text-muted">{p.department||'—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
